/**
 * StaffScheduledTemplates — Pagina Staff · Invii Template programmati.
 *
 * ADDITIVA — non modifica flow, componenti né UX esistente. Consuma
 * endpoint STAFF alias di quelli admin (stesso backend, permission
 * `staff.messaging.templates.send`).
 *
 * Route: /staff/scheduled-templates
 *
 * Due TAB:
 *   PROGRAMMATI → status=scheduled, azioni Dettagli/Modifica/Annulla
 *   STORICO     → status in {completed, partially_failed, failed, cancelled}
 *
 * Tablet-first: card impilate, touch target ≥44px, tab a pillole, modali
 * fluide senza sidebar. Nessun setInterval "cieco" — refresh manuale + al
 * cambio tab + auto-refresh 30s solo quando visibile.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Clock, CalendarClock, RefreshCw, AlertCircle, CheckCircle2, XCircle,
  Ban, Info, ArrowLeft, Pencil, Loader2, ChevronRight, Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { BatchFailuresList } from "@/components/admin/WhatsAppTemplateFlow";

const TZ = "Europe/Rome";

const STATUS_META = {
  scheduled:         { label: "Programmato",        Icon: Clock,         cls: "text-amber-800 bg-amber-50 border-amber-300" },
  processing:        { label: "In esecuzione",      Icon: Loader2,       cls: "text-sky-800 bg-sky-50 border-sky-300" },
  in_progress:       { label: "In esecuzione",      Icon: Loader2,       cls: "text-sky-800 bg-sky-50 border-sky-300" },
  completed:         { label: "Completato",         Icon: CheckCircle2,  cls: "text-emerald-800 bg-emerald-50 border-emerald-300" },
  partially_failed:  { label: "Parziale",           Icon: AlertCircle,   cls: "text-orange-800 bg-orange-50 border-orange-300" },
  failed:            { label: "Fallito",            Icon: XCircle,       cls: "text-rose-800 bg-rose-50 border-rose-300" },
  cancelled:         { label: "Annullato",          Icon: Ban,           cls: "text-slate-700 bg-slate-100 border-slate-300" },
  blocked:           { label: "Bloccato",           Icon: XCircle,       cls: "text-rose-800 bg-rose-50 border-rose-300" },
};

const PRIORITY_META = {
  high:   { label: "Alta",    cls: "text-rose-800 bg-rose-50 border-rose-300" },
  normal: { label: "Normale", cls: "text-slate-700 bg-slate-100 border-slate-300" },
  low:    { label: "Bassa",   cls: "text-slate-600 bg-slate-50 border-slate-200" },
};

function fmtUtcAsRome(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: TZ,
    }).format(d);
  } catch { return iso; }
}

function fmtDateOnly(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", timeZone: TZ,
    }).format(d);
  } catch { return iso; }
}

function fmtTimeOnly(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("it-IT", {
      hour: "2-digit", minute: "2-digit", timeZone: TZ,
    }).format(d);
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, Icon: Info, cls: "text-ink-600 bg-sand-100 border-sand-300" };
  const Icon = meta.Icon;
  return (
    <span data-testid={`sched-status-${status}`}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-medium ${meta.cls}`}>
      <Icon className="w-3 h-3" />{meta.label}
    </span>
  );
}

function PriorityBadge({ priority }) {
  if (!priority || priority === "normal") return null;
  const meta = PRIORITY_META[priority] || PRIORITY_META.normal;
  return (
    <span data-testid={`sched-prio-${priority}`}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

// ===========================================================================
// PAGINA PRINCIPALE
// ===========================================================================
export default function StaffScheduledTemplates() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canManage = hasPermission(user, "staff.messaging.templates.send");
  const [tab, setTab] = useState("scheduled");   // "scheduled" | "history"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [detailBatch, setDetailBatch] = useState(null);    // full batch doc
  const [rescheduleBatch, setRescheduleBatch] = useState(null); // batch da modificare
  const [cancelBatch, setCancelBatch] = useState(null);    // batch da annullare
  const mountedRef = useRef(true);

  const loadSeq = useRef(0);
  const load = useCallback(async (soft = false) => {
    if (!canManage) { setLoading(false); return; }
    const seq = ++loadSeq.current;
    if (soft) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const path = tab === "scheduled"
        ? "/staff/messaging/templates/scheduled?limit=100"
        : "/staff/messaging/templates/history?limit=200";
      const { data } = await api.get(path);
      if (!mountedRef.current || seq !== loadSeq.current) return;
      setItems(tab === "scheduled" ? (data.scheduled || []) : (data.history || []));
    } catch (e) {
      if (mountedRef.current && seq === loadSeq.current) setError(formatApiError(e));
    } finally {
      if (mountedRef.current && seq === loadSeq.current) { setLoading(false); setRefreshing(false); }
    }
  }, [tab, canManage]);

  useEffect(() => {
    mountedRef.current = true;
    load(false);
    return () => { mountedRef.current = false; };
  }, [load]);

  // Auto-refresh 30s SOLO su tab "Programmati" e quando la tab è visibile
  useEffect(() => {
    if (tab !== "scheduled" || !canManage) return;
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, 30000);
    return () => clearInterval(id);
  }, [tab, canManage, load]);

  const openDetail = useCallback(async (batchId) => {
    try {
      const { data } = await api.get(`/staff/messaging/templates/batches/${batchId}`);
      setDetailBatch(data);
      setItems((prev) => prev.map((b) => (b.batch_id === data.batch_id
        ? { ...b, status: data.status, totals: data.totals } : b)));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const doCancel = useCallback(async (batchId) => {
    try {
      await api.post(`/staff/messaging/templates/batches/${batchId}/cancel`);
      toast.success("Invio programmato annullato");
      setCancelBatch(null);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [load]);

  const doReschedule = useCallback(async (batchId, isoUtc) => {
    try {
      await api.post(`/staff/messaging/templates/batches/${batchId}/reschedule`, {
        scheduled_for_utc: isoUtc,
        scheduled_timezone: TZ,
      });
      toast.success("Invio riprogrammato");
      setRescheduleBatch(null);
      load(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [load]);

  if (!canManage) {
    return (
      <div data-testid="sched-forbidden" className="p-8 text-ink-500">
        Permesso <code>staff.messaging.templates.send</code> mancante.
      </div>
    );
  }

  return (
    <div data-testid="staff-scheduled-templates-page" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => nav("/staff")}
                    data-testid="sched-back-btn"
                    className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </button>
          <div className="eyebrow">WhatsApp</div>
          <h1 className="font-serif text-3xl mt-1">Invii programmati</h1>
          <p className="text-sm text-ink-500 mt-1">
            Batch Template programmati per data futura + storico esecuzioni.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => load(true)}
                  disabled={refreshing}
                  data-testid="sched-refresh-btn"
                  className="rounded-full">
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Aggiorna
        </Button>
      </div>

      {/* Tabs a pillole */}
      <div className="mb-5 inline-flex rounded-full border-2 border-sand-300 bg-white p-1"
             role="tablist">
        <button role="tab" aria-selected={tab === "scheduled"}
                  onClick={() => setTab("scheduled")}
                  data-testid="sched-tab-programmati"
                  className={`px-4 py-2 rounded-full text-sm font-medium min-w-[140px]
                              ${tab === "scheduled"
                                ? "bg-bronze-600 text-white"
                                : "text-ink-700 hover:bg-sand-100"}`}>
          Programmati
        </button>
        <button role="tab" aria-selected={tab === "history"}
                  onClick={() => setTab("history")}
                  data-testid="sched-tab-storico"
                  className={`px-4 py-2 rounded-full text-sm font-medium min-w-[140px]
                              ${tab === "history"
                                ? "bg-bronze-600 text-white"
                                : "text-ink-700 hover:bg-sand-100"}`}>
          Storico
        </button>
      </div>

      {/* Errori */}
      {error && (
        <div data-testid="sched-error" className="mb-4 rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* Contenuto tab */}
      {loading ? (
        <div data-testid="sched-loading" className="text-ink-500 py-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Caricamento…
        </div>
      ) : items.length === 0 ? (
        <EmptyState tab={tab} />
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="sched-list">
          {items.map((b) => (
            <BatchCard key={b.batch_id} batch={b} tab={tab}
                         onDetail={() => openDetail(b.batch_id)}
                         onReschedule={() => setRescheduleBatch(b)}
                         onCancel={() => setCancelBatch(b)} />
          ))}
        </div>
      )}

      {/* Dialog Dettagli */}
      <BatchDetailDialog batch={detailBatch}
                           onClose={() => setDetailBatch(null)} />

      {/* Dialog Riprogramma */}
      <RescheduleDialog batch={rescheduleBatch}
                          onClose={() => setRescheduleBatch(null)}
                          onSubmit={doReschedule} />

      {/* Dialog Annulla */}
      <CancelDialog batch={cancelBatch}
                      onClose={() => setCancelBatch(null)}
                      onConfirm={doCancel} />
    </div>
  );
}

// ===========================================================================
// Empty state
// ===========================================================================
function EmptyState({ tab }) {
  return (
    <div data-testid="sched-empty" className="rounded-3xl border-2 border-dashed border-sand-300 bg-white p-10 text-center">
      <CalendarClock className="w-12 h-12 mx-auto text-ink-300 mb-3" strokeWidth={1.5} />
      <div className="font-serif text-xl text-ink-700">
        {tab === "scheduled" ? "Nessun invio programmato" : "Nessuno storico disponibile"}
      </div>
      <div className="text-sm text-ink-500 mt-2 max-w-sm mx-auto">
        {tab === "scheduled"
          ? <>Programma un invio dal flow <b>Template WhatsApp</b> in Clienti scegliendo l'opzione <i>Programma invio</i>.</>
          : <>Gli invii chiusi (completati, parziali, falliti, annullati) compaiono qui.</>}
      </div>
    </div>
  );
}

// ===========================================================================
// Card batch
// ===========================================================================
function BatchCard({ batch, tab, onDetail, onReschedule, onCancel }) {
  const status = batch.status || "scheduled";
  const totals = batch.totals || {};
  const isScheduled = status === "scheduled";
  return (
    <div data-testid={`sched-card-${batch.batch_id}`}
           className="rounded-2xl border border-sand-300 bg-white p-4 md:p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={status} />
            {batch.is_overdue && (
              <span data-testid={`sched-overdue-${batch.batch_id}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border bg-rose-50 border-rose-300 text-rose-800 font-semibold">
                Scaduto · non avviato automaticamente
              </span>
            )}
            <PriorityBadge priority={batch.priority} />
            {batch.source && (
              <span className="text-[11px] text-ink-400 uppercase tracking-wide">{batch.source}</span>
            )}
          </div>
          <div className="mt-2 font-serif text-lg text-ink-800 break-all">
            {batch.template_name}
            {batch.template_language && (
              <span className="ml-2 text-xs text-ink-500 font-sans">
                [{batch.template_language}]
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-ink-600 flex flex-wrap gap-x-4 gap-y-1">
            {isScheduled ? (
              <span data-testid={`sched-when-${batch.batch_id}`} className="inline-flex items-center gap-1">
                <CalendarClock className="w-4 h-4" />
                {fmtDateOnly(batch.scheduled_for_utc)} · {fmtTimeOnly(batch.scheduled_for_utc)}
                <span className="text-ink-400">({batch.scheduled_timezone || TZ})</span>
              </span>
            ) : (
              <>
                {batch.scheduled_for_utc && (
                  <span className="inline-flex items-center gap-1 text-ink-500">
                    <CalendarClock className="w-4 h-4" /> Prog: {fmtUtcAsRome(batch.scheduled_for_utc)}
                  </span>
                )}
                {batch.completed_at && (
                  <span className="inline-flex items-center gap-1 text-ink-500">
                    <CheckCircle2 className="w-4 h-4" /> Eseg: {fmtUtcAsRome(batch.completed_at)}
                  </span>
                )}
              </>
            )}
            <span className="inline-flex items-center gap-1">
              <Users className="w-4 h-4" />
              {batch.final_recipient_count ?? totals.eligible ?? 0} destinatari
            </span>
          </div>
          {batch.note_operatore && (
            <div className="mt-2 text-sm text-ink-700 border-l-2 border-bronze-300 pl-3 italic">
              {batch.note_operatore}
            </div>
          )}
          {!isScheduled && (
            <div className="mt-2 text-xs text-ink-500 flex flex-wrap gap-x-4 gap-y-0.5">
              <span>Inviati: <b className="text-emerald-700">{totals.sent ?? 0}</b></span>
              <span>Consegnati: <b className="text-emerald-700">{totals.delivered ?? 0}</b></span>
              <span>Letti: <b className="text-sky-700">{totals.read ?? 0}</b></span>
              <span>Falliti: <b className="text-rose-700">{totals.failed ?? 0}</b></span>
              <span>Incerti: <b className="text-slate-600">{totals.unknown ?? 0}</b></span>
              {(totals.skipped ?? 0) > 0 && (
                <span>Saltati (consenso): <b className="text-amber-700">{totals.skipped}</b></span>
              )}
              {batch.error_summary && (
                <span className="text-rose-700">· {batch.error_summary}</span>
              )}
            </div>
          )}
          {batch.created_by_user_id && (
            <div className="mt-1 text-[11px] text-ink-400">
              Creato da: {batch.created_by_user_id.slice(0, 8)}…
            </div>
          )}
        </div>
      </div>

      {/* Azioni */}
      <div className="mt-3 flex gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={onDetail}
                  data-testid={`sched-detail-btn-${batch.batch_id}`}
                  className="rounded-full min-h-[40px]">
          <Info className="w-4 h-4 mr-1" /> Dettagli
        </Button>
        {isScheduled && (
          <>
            <Button size="sm" variant="outline" onClick={onReschedule}
                      data-testid={`sched-reschedule-btn-${batch.batch_id}`}
                      className="rounded-full min-h-[40px] border-bronze-400 text-bronze-800 hover:bg-bronze-50">
              <Pencil className="w-4 h-4 mr-1" /> Modifica
            </Button>
            <Button size="sm" variant="outline" onClick={onCancel}
                      data-testid={`sched-cancel-btn-${batch.batch_id}`}
                      className="rounded-full min-h-[40px] border-rose-400 text-rose-800 hover:bg-rose-50">
              <Ban className="w-4 h-4 mr-1" /> Annulla
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Dialog Dettagli
// ===========================================================================
function BatchDetailDialog({ batch, onClose }) {
  const [phones, setPhones] = useState(false);
  if (!batch) return null;
  const totals = batch.totals || {};
  const recipientPhones = batch.final_recipient_phones || [];
  const recipientNames = batch.final_recipient_display_names || [];
  return (
    <Dialog open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sched-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Dettagli invio programmato</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <StatusBadge status={batch.status} />
            <PriorityBadge priority={batch.priority} />
          </div>
          <Row k="Template" v={<code>{batch.template_name} [{batch.template_language}]</code>} />
          <Row k="Programmato per" v={<>{fmtUtcAsRome(batch.scheduled_for_utc)} <span className="text-ink-400">({batch.scheduled_timezone || TZ})</span></>} />
          {batch.actual_start_at && <Row k="Avvio effettivo" v={fmtUtcAsRome(batch.actual_start_at)} />}
          {batch.completed_at && <Row k="Completato" v={fmtUtcAsRome(batch.completed_at)} />}
          {batch.cancelled_at && <Row k="Annullato" v={fmtUtcAsRome(batch.cancelled_at)} />}
          {typeof batch.late_by_seconds === "number" && (
            <Row k="Ritardo cron" v={`${batch.late_by_seconds}s`} />
          )}
          <Row k="Destinatari (snapshot)" v={<b>{batch.final_recipient_count ?? recipientPhones.length ?? 0}</b>} />
          {batch.note_operatore && <Row k="Note operatore" v={batch.note_operatore} />}
          {batch.error_summary && (
            <Row k="Errore" v={<span className="text-rose-700">{batch.error_summary}</span>} />
          )}
          <div className="pt-2 border-t border-sand-200">
            <div className="text-xs font-medium text-ink-600 mb-1">Totali</div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Metric label="Selezionati" v={totals.selected ?? 0} />
              <Metric label="Idonei" v={totals.eligible ?? 0} />
              <Metric label="Esclusi" v={totals.excluded ?? 0} />
              <Metric label="Inviati" v={totals.sent ?? 0} cls="text-emerald-700" />
              <Metric label="Consegnati" v={totals.delivered ?? 0} cls="text-emerald-700" />
              <Metric label="Letti" v={totals.read ?? 0} cls="text-sky-700" />
              <Metric label="Falliti" v={totals.failed ?? 0} cls="text-rose-700" />
              <Metric label="Incerti" v={totals.unknown ?? 0} />
              <Metric label="Saltati (consenso)" v={totals.skipped ?? 0} cls="text-amber-700" />
              <Metric label="In attesa conferma" v={totals.current_states?.sent ?? 0} />
            </div>
          </div>
          <BatchFailuresList failures={batch.failures} />

          {recipientPhones.length > 0 && (
            <div>
              <button onClick={() => setPhones(v => !v)}
                        data-testid="sched-toggle-phones"
                        className="text-xs text-bronze-700 hover:underline">
                {phones ? "Nascondi" : "Mostra"} snapshot destinatari ({recipientPhones.length})
              </button>
              {phones && (
                <div data-testid="sched-phones-list"
                       className="mt-2 max-h-40 overflow-auto rounded-lg border border-sand-200 bg-sand-50 p-2 text-[11px] font-mono">
                  {recipientPhones.map((p, i) => (
                    <div key={i} className="truncate">
                      {p} {recipientNames[i] ? `— ${recipientNames[i]}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(batch.rescheduled_history || []).length > 0 && (
            <div>
              <div className="text-xs font-medium text-ink-600 mb-1">Storico riprogrammazioni</div>
              <div className="text-[11px] text-ink-500 space-y-0.5">
                {batch.rescheduled_history.map((h, i) => (
                  <div key={i}>
                    {fmtUtcAsRome(h.from_utc)} → {fmtUtcAsRome(h.to_utc)} · {fmtUtcAsRome(h.at)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="sched-detail-close-btn"
                    className="rounded-full">Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-40 flex-shrink-0 text-ink-500">{k}</div>
      <div className="flex-1 text-ink-800 break-words">{v}</div>
    </div>
  );
}

function Metric({ label, v, cls = "text-ink-700" }) {
  return (
    <div className="rounded-lg bg-sand-50 border border-sand-200 p-2">
      <div className="text-[10px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`font-serif text-lg ${cls}`}>{v}</div>
    </div>
  );
}

// ===========================================================================
// Dialog Riprogramma
// ===========================================================================
function RescheduleDialog({ batch, onClose, onSubmit }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (batch?.scheduled_for_utc) {
      // Precompila con orario Rome corrente del batch
      try {
        const d = new Date(batch.scheduled_for_utc);
        const local = new Date(d.toLocaleString("en-US", { timeZone: TZ }));
        const yyyy = local.getFullYear();
        const mm = String(local.getMonth() + 1).padStart(2, "0");
        const dd = String(local.getDate()).padStart(2, "0");
        const hh = String(local.getHours()).padStart(2, "0");
        const mi = String(local.getMinutes()).padStart(2, "0");
        setDate(`${yyyy}-${mm}-${dd}`);
        setTime(`${hh}:${mi}`);
      } catch {/* noop */}
    }
  }, [batch]);

  const isoUtc = useMemo(() => {
    if (!date || !time) return null;
    try {
      // Interpret input as Rome-local, produce UTC ISO
      const romeIso = `${date}T${time}:00`;
      const romeAsUtc = new Date(romeIso + "Z");    // trick per parse pulita
      // Compute offset by rendering back as Rome and computing delta
      const dRome = new Date(new Intl.DateTimeFormat("en-US", {
        timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
      }).format(romeAsUtc));
      const offsetMin = (romeAsUtc - dRome) / 60000;
      const asUtc = new Date(romeAsUtc.getTime() - (-offsetMin) * 60000);
      return asUtc.toISOString();
    } catch { return null; }
  }, [date, time]);

  const validation = useMemo(() => {
    if (!isoUtc) return { ok: false, msg: "Compila data e ora" };
    const target = new Date(isoUtc).getTime();
    const nowPlus2 = Date.now() + 2 * 60 * 1000;
    if (target < Date.now()) return { ok: false, msg: "Data nel passato" };
    if (target < nowPlus2) return { ok: false, msg: "Serve almeno +2 minuti" };
    return { ok: true };
  }, [isoUtc]);

  if (!batch) return null;

  return (
    <Dialog open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sched-reschedule-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Riprogramma invio</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-ink-600">
            Template: <b>{batch.template_name}</b> · Snapshot destinatari
            immutato ({batch.final_recipient_count} clienti).
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-700 mb-1">Data</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                     data-testid="sched-reschedule-date" className="min-h-[44px]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-700 mb-1">Ora</label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
                     data-testid="sched-reschedule-time" className="min-h-[44px]" />
          </div>
          <div className="text-xs text-ink-500">Fuso: <b>{TZ}</b></div>
          {!validation.ok && (
            <div data-testid="sched-reschedule-error" className="text-xs text-rose-700">
              {validation.msg}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full"
                    data-testid="sched-reschedule-cancel">Annulla</Button>
          <Button onClick={async () => {
                    if (!validation.ok || submitting) return;
                    setSubmitting(true);
                    await onSubmit(batch.batch_id, isoUtc);
                    setSubmitting(false);
                  }}
                    disabled={!validation.ok || submitting}
                    data-testid="sched-reschedule-submit"
                    className="rounded-full bg-bronze-600 hover:bg-bronze-700 text-white">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}
            Riprogramma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Dialog Annulla
// ===========================================================================
function CancelDialog({ batch, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  if (!batch) return null;
  return (
    <Dialog open={!!batch} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sched-cancel-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Annullare l'invio programmato?</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div className="text-ink-700">
            Template: <b>{batch.template_name}</b>
          </div>
          <div className="text-ink-700">
            Data: <b>{fmtUtcAsRome(batch.scheduled_for_utc)}</b>
          </div>
          <div className="text-ink-700">
            Destinatari programmati: <b>{batch.final_recipient_count}</b>
          </div>
          <div className="text-xs text-rose-700 mt-2 rounded-lg bg-rose-50 border border-rose-200 p-2">
            L'annullamento è irreversibile. Il batch NON verrà eseguito e passerà a
            stato <code>cancelled</code>.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full"
                    data-testid="sched-cancel-back">Indietro</Button>
          <Button onClick={async () => {
                    if (submitting) return;
                    setSubmitting(true);
                    await onConfirm(batch.batch_id);
                    setSubmitting(false);
                  }}
                    disabled={submitting}
                    data-testid="sched-cancel-confirm"
                    className="rounded-full bg-rose-600 hover:bg-rose-700 text-white">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
            Sì, annulla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
